import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../dashboard_component';
import { labelAPI } from '../../api';

// Predefined icons for folder system
const ICON_OPTIONS = [
  { value: 'folder', label: '📁 Folder', component: '📁' },
  { value: 'document', label: '📄 Document', component: '📄' },
  { value: 'star', label: '⭐ Star', component: '⭐' },
  { value: 'heart', label: '❤️ Heart', component: '❤️' },
  { value: 'tag', label: '🏷️ Tag', component: '🏷️' },
  { value: 'bookmark', label: '🔖 Bookmark', component: '🔖' },
  { value: 'lock', label: '🔒 Lock', component: '🔒' },
  { value: 'folder-open', label: '📂 Open Folder', component: '📂' }
];

// Predefined color swatches for label customization
const COLOR_OPTIONS = [
  '#EF4444', // Red
  '#F59E0B', // Amber
  '#10B981', // Emerald
  '#3B82F6', // Blue
  '#8B5CF6', // Violet
  '#EC4899', // Pink
  '#6B7280', // Gray
  '#1F2937'  // Dark
];

const LabelSelector = ({ value, onChange, allowCreate = true }) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('labels');
  
  // Label state
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState(value || null);
  
  // Create label form state
  const [newLabelName, setNewLabelName] = useState('');
  const [selectedIcon, setSelectedIcon] = useState(ICON_OPTIONS[0].value);
  const [selectedColor, setSelectedColor] = useState(COLOR_OPTIONS[2]);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  // Edit label state (inline editing)
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingIcon, setEditingIcon] = useState('');
  const [editingColor, setEditingColor] = useState('');

  // Helper: Get icon component by value
  const getIconComponent = (iconValue) => {
    const icon = ICON_OPTIONS.find(opt => opt.value === iconValue);
    return icon ? icon.component : '📁';
  };

  // Fetch all labels from backend
  const fetchLabels = async () => {
    try {
      setLoading(true);
      const res = await labelAPI.getLabels();
      // Ensure we always set an array
      const fetchedLabels = Array.isArray(res.data) ? res.data : [];
      setLabels(fetchedLabels);
    } catch (error) {
      console.error('Failed to load labels', error);
      toast.error('Failed to load labels');
      setLabels([]);
    } finally {
      setLoading(false);
    }
  };

  // Create new label
  const handleCreateLabel = async () => {
    if (!newLabelName.trim()) {
      toast.error('Please enter a label name');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        name: newLabelName,
        color: selectedColor,
        icon: selectedIcon
      };
      const res = await labelAPI.createLabel(payload);
      if (!res.data || !res.data.label_id) {
        throw new Error('Invalid response from server');
      }
      
      // Refetch labels to get the latest list
      await fetchLabels();
      
      // Auto-select newly created label if onChange exists
      if (onChange) {
        onChange(res.data.label_id);
        setSelectedLabelId(res.data.label_id);
      }
      
      toast.success('Label created successfully!');
      // Reset form
      setNewLabelName('');
      setSelectedIcon(ICON_OPTIONS[0].value);
      setSelectedColor(COLOR_OPTIONS[0]);
      setActiveTab('labels');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create label';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Delete label
  const handleDeleteLabel = async (labelId, labelName) => {
    if (!window.confirm(`Are you sure you want to delete label "${labelName}"?`)) return;
    
    try {
      setLoading(true);
      await labelAPI.deleteLabel(labelId);
      // Refetch after deletion
      await fetchLabels();
      if (selectedLabelId === labelId) {
        setSelectedLabelId(null);
        if (onChange) onChange(null);
      }
      toast.success('Label deleted');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete label';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Start editing a label
  const startEditLabel = (label) => {
    setEditingLabelId(label.label_id);
    setEditingName(label.name);
    setEditingIcon(label.icon || 'folder');
    setEditingColor(label.color || '#3B82F6');
  };

  // Save edited label
  const saveEditLabel = async () => {
    if (!editingName.trim()) {
      toast.error('Label name cannot be empty');
      return;
    }
    
    setLoading(true);
    try {
      const payload = { name: editingName };
      if (editingIcon) payload.icon = editingIcon;
      if (editingColor) payload.color = editingColor;
      
      const res = await labelAPI.updateLabel(editingLabelId, payload);
      if (!res.data || !res.data.label_id) {
        throw new Error('Invalid response from server');
      }
      
      // Refetch to get updated data
      await fetchLabels();
      toast.success('Label updated');
      cancelEdit();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to update label';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingLabelId(null);
    setEditingName('');
    setEditingIcon('');
    setEditingColor('');
    setShowIconPicker(false);
    setShowColorPicker(false);
  };

  // Select a label (for parent form usage)
  const selectLabel = (labelId) => {
    setSelectedLabelId(labelId);
    if (onChange) {
      onChange(labelId);
    }
    const selected = labels.find(l => l.label_id === labelId);
    if (selected) toast.info(`Selected label: ${selected.name}`);
  };

  useEffect(() => {
    fetchLabels();
  }, []);

  useEffect(() => {
    if (value !== undefined && value !== selectedLabelId) {
      setSelectedLabelId(value);
    }
  }, [value]);

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <div 
        className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${
          isCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-grow w-full">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Label Manager</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Organize your content with custom labels, folder icons, and color coding
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="border-b border-gray-200 w-full max-w-6xl">
              <nav className="-mb-px flex flex-wrap space-x-2 md:space-x-8">
                <button
                  onClick={() => setActiveTab('labels')}
                  className={`py-4 px-1 text-center border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'labels'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  📂 My Labels (Folder View)
                </button>
                {allowCreate && (
                  <button
                    onClick={() => setActiveTab('create')}
                    className={`py-4 px-1 text-center border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === 'create'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    ✨ Create New Label
                  </button>
                )}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="w-full">
            {/* Labels Tab - Folder System View */}
            {activeTab === 'labels' && (
              <div className="bg-white rounded-lg shadow p-6 max-w-6xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-indigo-100 rounded-full mr-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800">Folder View • All Labels</h2>
                  </div>
                  {selectedLabelId && (
                    <div className="bg-blue-50 px-3 py-2 rounded-lg text-sm text-blue-700">
                      Current selection: {labels.find(l => l.label_id === selectedLabelId)?.name || 'None'}
                    </div>
                  )}
                </div>

                {loading && labels.length === 0 ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                  </div>
                ) : labels.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📂</div>
                    <h3 className="text-lg font-medium text-gray-900">No labels yet</h3>
                    <p className="mt-1 text-gray-500">Get started by creating your first label.</p>
                    {allowCreate && (
                      <button
                        onClick={() => setActiveTab('create')}
                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                      >
                        Create Label
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {labels.map((label) => {
                      // Guard against undefined label (should not happen, but safe)
                      if (!label || !label.label_id) return null;
                      const isEditing = editingLabelId === label.label_id;
                      const iconChar = getIconComponent(label.icon);
                      const labelColor = label.color || '#3B82F6';
                      
                      return (
                        <div
                          key={label.label_id}
                          className={`group relative bg-white border rounded-xl shadow-sm hover:shadow-md transition-all duration-200 ${
                            selectedLabelId === label.label_id ? 'ring-2 ring-indigo-500 border-indigo-200' : 'border-gray-200'
                          }`}
                        >
                          {isEditing ? (
                            <div className="p-4 space-y-3">
                              <input
                                type="text"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                autoFocus
                              />
                              <div className="flex items-center gap-2">
                                <div className="relative">
                                  <button
                                    onClick={() => setShowIconPicker(!showIconPicker)}
                                    className="px-3 py-2 border rounded-lg text-sm bg-gray-50 flex items-center gap-2"
                                  >
                                    <span>{iconChar}</span> Change Icon
                                  </button>
                                  {showIconPicker && (
                                    <div className="absolute z-10 mt-1 w-48 bg-white border rounded-lg shadow-lg p-2 grid grid-cols-4 gap-1">
                                      {ICON_OPTIONS.map(icon => (
                                        <button
                                          key={icon.value}
                                          onClick={() => {
                                            setEditingIcon(icon.value);
                                            setShowIconPicker(false);
                                          }}
                                          className="p-2 hover:bg-gray-100 rounded text-xl"
                                          title={icon.label}
                                        >
                                          {icon.component}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="relative">
                                  <button
                                    onClick={() => setShowColorPicker(!showColorPicker)}
                                    className="px-3 py-2 border rounded-lg text-sm bg-gray-50 flex items-center gap-2"
                                  >
                                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: editingColor }}></div>
                                    Color
                                  </button>
                                  {showColorPicker && (
                                    <div className="absolute z-10 mt-1 bg-white border rounded-lg shadow-lg p-2 grid grid-cols-4 gap-2">
                                      {COLOR_OPTIONS.map(color => (
                                        <button
                                          key={color}
                                          onClick={() => {
                                            setEditingColor(color);
                                            setShowColorPicker(false);
                                          }}
                                          className="w-6 h-6 rounded-full border hover:scale-110 transition"
                                          style={{ backgroundColor: color }}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2 pt-2">
                                <button
                                  onClick={saveEditLabel}
                                  disabled={loading}
                                  className="flex-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="p-4">
                              <div className="flex items-start justify-between">
                                <div
                                  className="cursor-pointer flex-1"
                                  onClick={() => selectLabel(label.label_id)}
                                >
                                  <div
                                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-3"
                                    style={{ backgroundColor: `${labelColor}20` }}
                                    onClick={() => navigate(`/cbt_creator/cbts?label_id=${label.label_id}`)}
                                  >
                                    <span style={{ color: labelColor }}>{iconChar}</span>
                                  </div>
                                  <h3 className="font-medium text-gray-900 truncate" title={label.name}>
                                    {label.name}
                                  </h3>
                                  <p className="text-xs text-gray-500 mt-1">ID: {label.label_id}</p>
                                </div>
                                <div className="flex gap-1 opacity-80 group-hover:opacity-100 transition">
                                  <button
                                    onClick={() => startEditLabel(label)}
                                    className="p-1.5 text-gray-900 hover:text-indigo-600 rounded-lg"
                                    title="Edit label"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteLabel(label.label_id, label.name)}
                                    className="p-1.5 text-gray-900 hover:text-red-600 rounded-lg"
                                    title="Delete label"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                              {selectedLabelId === label.label_id && (
                                <div className="mt-3 pt-2 border-t border-gray-100">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                    Selected
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Create Label Tab */}
            {activeTab === 'create' && allowCreate && (
              <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto">
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-green-100 rounded-full mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-800">Create New Label</h2>
                </div>
                
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Label Name</label>
                    <input
                      type="text"
                      value={newLabelName}
                      onChange={(e) => setNewLabelName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., Important, Work, Personal"
                      autoFocus
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Folder Icon</label>
                      <div className="relative">
                        <button
                          onClick={() => setShowIconPicker(!showIconPicker)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white flex items-center justify-between"
                        >
                          <span className="flex items-center gap-2">
                            <span className="text-xl">{getIconComponent(selectedIcon)}</span>
                            <span>{ICON_OPTIONS.find(opt => opt.value === selectedIcon)?.label}</span>
                          </span>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {showIconPicker && (
                          <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg p-2 grid grid-cols-4 gap-1">
                            {ICON_OPTIONS.map(icon => (
                              <button
                                key={icon.value}
                                onClick={() => {
                                  setSelectedIcon(icon.value);
                                  setShowIconPicker(false);
                                }}
                                className="p-2 hover:bg-gray-100 rounded text-xl"
                                title={icon.label}
                              >
                                {icon.component}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Color Theme</label>
                      <div className="relative">
                        <button
                          onClick={() => setShowColorPicker(!showColorPicker)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white flex items-center justify-between"
                        >
                          <span className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full" style={{ backgroundColor: selectedColor }}></div>
                            <span>{selectedColor}</span>
                          </span>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {showColorPicker && (
                          <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg p-3">
                            <div className="grid grid-cols-4 gap-2">
                              {COLOR_OPTIONS.map(color => (
                                <button
                                  key={color}
                                  onClick={() => {
                                    setSelectedColor(color);
                                    setShowColorPicker(false);
                                  }}
                                  className="w-full aspect-square rounded-lg border hover:scale-105 transition"
                                  style={{ backgroundColor: color }}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                        style={{ backgroundColor: `${selectedColor}20` }}
                      >
                        <span style={{ color: selectedColor }}>{getIconComponent(selectedIcon)}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{newLabelName || 'Label Name'}</p>
                        <p className="text-xs text-gray-500">New label preview</p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleCreateLabel}
                    disabled={loading || !newLabelName.trim()}
                    className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                      loading || !newLabelName.trim() ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating...
                      </span>
                    ) : (
                      'Create Label'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className="mt-8 bg-blue-50 border-l-4 border-blue-400 p-4 rounded max-w-6xl mx-auto w-full">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Label Management Tips</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc pl-5 space-y-1">
                    <li><span className="font-medium">Folder View:</span> Each label is displayed as a folder card with custom icon and color</li>
                    <li><span className="font-medium">Select Label:</span> Click on any label card to select it (used in forms)</li>
                    <li><span className="font-medium">Customize:</span> Hover over a label and click the edit icon to change name, icon, or color</li>
                    <li><span className="font-medium">Create New:</span> Use the "Create New Label" tab to add labels with custom icons and colors</li>
                    <li><span className="font-medium">Delete Protection:</span> The "Default" label cannot be deleted, ensuring you always have at least one label</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </div>
      </div>
    </div>
  );
};

export default LabelSelector;